import React, {useEffect, useState, useRef, type ReactElement} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {DownloadPanelPublishingSection} from '@Components/download-panel/components/download-panel-publishing-section/download-panel-publishing-section';
import styles from './download-panel-publishing-section-wrapper.module.scss';

interface DownloadPanelPublishingSectionWrapperProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  isVisible: boolean;
}

export function DownloadPanelPublishingSectionWrapper({
  containerRef,
  wrapperRef,
  isVisible,
}: DownloadPanelPublishingSectionWrapperProps): ReactElement | null {
  const [publishingTop, setPublishingTop] = useState<number | undefined>(undefined);
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (!isVisible) {
      setPublishingTop(undefined);
      return;
    }

    const targetEl = containerRef.current ?? wrapperRef.current;
    if (!targetEl) {
      setPublishingTop(undefined);
      return;
    }

    const calculatePosition = (): void => {
      requestAnimationFrame(() => {
        const el = containerRef.current ?? wrapperRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        setPublishingTop(rect.bottom + 12);
      });
    };

    const timeoutId = setTimeout(() => {
      calculatePosition();
    }, 0);

    const resizeObserver = new ResizeObserver(() => {
      calculatePosition();
    });

    resizeObserver.observe(targetEl);

    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition, true);

    return (): void => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition, true);
    };
  }, [isVisible, containerRef, wrapperRef]);

  useEffect(() => {
    if (isVisible) {
      const id = setTimeout(() => {
        isMountedRef.current = true;
      }, 0);
      return () => {
        clearTimeout(id);
      };
    }
    isMountedRef.current = false;
    return undefined;
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      {isMountedRef.current ? (
        <motion.div
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          transition={{duration: 0}}
          style={{
            top: publishingTop,
          }}
          className={styles.webPublishingSection}
        >
          <DownloadPanelPublishingSection />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
